import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Pencil, Trash2, PlusCircle, CheckCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';

const STATUS_COLORS = {
    New: 'bg-blue-100 text-blue-800',
    Contacted: 'bg-indigo-100 text-indigo-800',
    Qualified: 'bg-purple-100 text-purple-800',
    Pending: 'bg-yellow-100 text-yellow-800',
    Converted: 'bg-green-100 text-green-800',
    Lost: 'bg-red-100 text-red-800',
};

const STATUSES = ['New', 'Contacted', 'Qualified', 'Pending', 'Converted', 'Lost'];

export default function LeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);

    useEffect(() => {
        api.get(`/leads/${id}`)
            .then(res => setLead(res.data))
            .catch(() => toast.error('Failed to load lead'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleStatusChange = async (status) => {
        setUpdatingStatus(true);
        try {
            const res = await api.put(`/leads/${id}`, { status });
            setLead(res.data);
            toast.success('Status updated');
        } catch {
            toast.error('Failed to update status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const handleSaveNote = async () => {
        if (!newNote.trim()) return;
        setSavingNote(true);
        try {
            const combined = lead.notes ? `${lead.notes}\n\n[${new Date().toLocaleString()}] ${newNote}` : `[${new Date().toLocaleString()}] ${newNote}`;
            const res = await api.put(`/leads/${id}`, { notes: combined });
            setLead(res.data);
            setNewNote('');
            toast.success('Note saved');
        } catch {
            toast.error('Failed to save note');
        } finally {
            setSavingNote(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this lead permanently?')) return;
        try {
            await api.delete(`/leads/${id}`);
            toast.success('Lead deleted');
            navigate('/admin/leads');
        } catch {
            toast.error('Failed to delete lead');
        }
    };

    if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;
    if (!lead) return <div className="text-center py-20 text-gray-400">Lead not found</div>;

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <Toaster position="top-right" richColors />
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate('/admin/leads')} className="p-2 hover:bg-gray-200 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{lead.name}</h2>
                            <p className="text-sm text-gray-500">{lead.service_requested}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => navigate(`/admin/leads/${id}/edit`)} className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                            <Pencil className="w-4 h-4" /> Edit
                        </button>
                        <button onClick={handleDelete} className="inline-flex items-center gap-2 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-100">
                            <Trash2 className="w-4 h-4" /> Delete
                        </button>
                    </div>
                </div>

                {/* Lead Info */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        ['Phone', lead.phone],
                        ['Email', lead.email || '—'],
                        ['City', lead.city || '—'],
                        ['Insurance', lead.insurance_type || '—'],
                        ['Diagnosis', lead.diagnosis || '—'],
                        ['Referral Source', lead.referral_source?.facility_name || '—'],
                        ['Assigned Staff', lead.assigned_staff?.name || '—'],
                        ['Created', new Date(lead.created_at).toLocaleString()],
                    ].map(([label, value]) => (
                        <div key={label}>
                            <p className="text-xs font-semibold text-gray-400 uppercase">{label}</p>
                            <p className="text-gray-800 mt-0.5">{value}</p>
                        </div>
                    ))}
                </div>

                {/* Status Change */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-700 mb-3">Status</h2>
                    <div className="flex flex-wrap gap-2">
                        {STATUSES.map(s => (
                            <button
                                key={s}
                                disabled={updatingStatus}
                                onClick={() => handleStatusChange(s)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${lead.status === s ? STATUS_COLORS[s] + ' border-current' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Notes */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <h2 className="font-semibold text-gray-700 mb-3">Notes</h2>
                    {lead.notes ? (
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 rounded-lg p-4 mb-4 font-sans">{lead.notes}</pre>
                    ) : (
                        <p className="text-gray-400 text-sm mb-4">No notes yet.</p>
                    )}
                    <textarea
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        rows={3}
                        placeholder="Add a new note…"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                        onClick={handleSaveNote}
                        disabled={savingNote || !newNote.trim()}
                        className="mt-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50 transition"
                    >
                        {savingNote ? 'Saving…' : 'Save Note'}
                    </button>
                </div>

                {/* Linked Tasks */}
                {lead.tasks?.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                        <h2 className="font-semibold text-gray-700 mb-3">Linked Tasks</h2>
                        <ul className="divide-y divide-gray-100">
                            {lead.tasks.map(t => (
                                <li key={t.id} className="py-2 flex items-center justify-between">
                                    <div>
                                        <p className={`font-medium ${t.is_completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>{t.title}</p>
                                        {t.due_date && <p className="text-xs text-gray-400">Due: {new Date(t.due_date).toLocaleDateString()}</p>}
                                    </div>
                                    {t.is_completed && <CheckCircle className="w-4 h-4 text-green-500" />}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
